import type { GetServerSidePropsResult } from 'next';
import { getPageCounter } from 'shared/prometheus/prometheus';
import { fetchHotel, fetchOffersForHotel } from '@/api/fetcher';
import { EditPanel } from '@/components/filters/EditPanel';
import HotelOriginDropdown from '@/components/filters/origin/HotelOriginDropdown';
import HotelTravelTimePicker from '@/components/filters/travelTime/HotelTravelTimePicker';
import HotelDescription from '@/components/hotel/HotelDescription';
import HotelFilterPreview from '@/components/hotel/HotelFilterPreview';
import HotelLayout from '@/components/hotel/HotelLayout';
import HotelPagination from '@/components/hotel/HotelPagination';
import HotelResultList from '@/components/hotel/HotelResultList';
import { config, feConfig } from '@/config';
import type { Filters, HotelResults } from '@/interfaces';
import { browserQueryParamsToFilters, filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { runAllMiddlewares } from '@/server/middleware';
import { actions } from '@/state/filterActions';
import { actions as hotelActions } from '@/state/hotelActions';
import { actions as hotelResultActions } from '@/state/hotelResultActions';
import { wrapper } from '@/state/store';

const getPreferredSupplier = (hotelResults: HotelResults): string | undefined =>
    hotelResults.offers.length > 0 ? hotelResults.offers[0].supplier : undefined;

const HotelPage = (props) => {
    wrapper.useHydration(props);

    return (
        <HotelLayout>
            <HotelDescription />
            <h2 className="u-screen-reader-only">Alternativer for avreise og hjemreise</h2>
            <EditPanel preview={<HotelFilterPreview />}>
                <div className="edit-panel-hotelfilters finn-reise--pakkereiser-hotell" data-testid="hotelFilter">
                    <div className="align-bottom u-r-size1of3 u-pr8--gt768">
                        <HotelOriginDropdown />
                    </div>
                    <div className="align-bottom u-r-size2of3">
                        <HotelTravelTimePicker />
                    </div>
                </div>
            </EditPanel>
            <HotelResultList />
            <HotelPagination />
        </HotelLayout>
    );
};

export const getServerSideProps = wrapper.getServerSideProps(
    (store) =>
        // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
        async ({ req, res, query }): Promise<GetServerSidePropsResult<any>> => {
            const props = await runAllMiddlewares(req, res, query);

            const filters: Partial<Filters> = browserQueryParamsToFilters(query);
            store.dispatch(actions.filter(filters));

            const apiQueryParams = filtersToApiQueryParams(filters);
            const { statusCode: offerStatusCode, data: offerData } = await fetchOffersForHotel(config.get('apiUrl'), apiQueryParams);
            const preferredSupplier = offerStatusCode === 200 ? getPreferredSupplier(offerData as HotelResults) : undefined;
            const { statusCode: hotelStatusCode, data: hotelData } = await fetchHotel(config.get('apiUrl'), {
                ...apiQueryParams,
                preferredSupplier,
            });

            if (hotelStatusCode === 200) {
                store.dispatch(hotelActions.setHotel(hotelData));
            }

            if (offerStatusCode === 200) {
                store.dispatch(hotelResultActions.setResults(offerData));
            } else {
                store.dispatch(hotelResultActions.setError());
            }
            getPageCounter().inc({ app: 'pale-pp-web', page: 'hotell' });
            return { props: { ...props, config: feConfig } };
        },
);

export default HotelPage;
